import { MassUnits } from "@/libs/units/mass";
import { PercentageUnits } from "@/libs/units/percentage";
import { PowerUnits } from "@/libs/units/power";
import { EquipmentCalculatorSectionKey } from "../../calculationTypes";
import type { ConverterInputProps } from "@/components/types";
import type { Section, SectionDeps } from "../types";
import { ScenarioDetailSectionKey, ScenarioDetailSectionTitle } from "../types";

export const buildThrustChamberSideCalculatorSection = ({
  scenarioCalculations,
  getHydraulicPowerUnit
}: SectionDeps): Section => ({
  id: EquipmentCalculatorSectionKey.THRUST_CHAMBER,
  hash: ScenarioDetailSectionKey.SEAL_SENSOR_MLE,
  label: ScenarioDetailSectionTitle.THRUST_CHAMBER,
  routeName: "SizingParameters",
  aggregateDisplays: [
    {
      label: "HP",
      base: PowerUnits.HorsePower,
      target: getHydraulicPowerUnit().value,
      model: PowerUnits.options(),
      value:
        scenarioCalculations.sealCalculations.aggregate.sealOperatingHP.value,
      mode: "read"
    },
    {
      label: "Thrust Load at Duty Point",
      base: MassUnits.Pounds,
      model: MassUnits.options(),
      value:
        scenarioCalculations.pumpCalculations.aggregate.thrustLoadPounds.value,
      mode: "convert"
    },
    {
      label: "Max Thrust Load",
      base: MassUnits.Pounds,
      model: MassUnits.options(),
      value:
        scenarioCalculations.pumpCalculations.aggregate.shutInThrustLoadPounds
          .value,
      mode: "convert"
    },
    {
      label: "Shut-in Bearing Load Ratio",
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      value:
        scenarioCalculations.sealCalculations.aggregate
          .sealShutInBearingLoadRatio.value,
      mode: "read"
    }
  ],
  individualDisplays: scenarioCalculations.descendingOrderSeals.value.map(
    (xrefSeal, index) => {
      const calcs =
        scenarioCalculations.sealCalculations.individual.value[xrefSeal?.id];
      // core displays for every seal
      const displays: ConverterInputProps[] = [
        {
          label: "Shaft Load %",
          base: PercentageUnits.Ratio,
          target: PercentageUnits.Percentage,
          model: PercentageUnits.options(),
          value: calcs.xrefSeal?.use_high_strength_shaft
            ? calcs.highStrengthShaftRatingRatio
            : calcs.standardShaftRatingRatio,
          mode: "read"
        },
        {
          label: "Bearing Rating @ 3500",
          base: MassUnits.Pounds,
          model: MassUnits.options(),
          value: calcs.bearingRatingAt60hzLBS,
          mode: "convert"
        },
        {
          label: "Bearing Rating @ RPM",
          base: MassUnits.Pounds,
          model: MassUnits.options(),
          value: calcs.bearingRatingAtRPMLBS,
          mode: "convert"
        }
      ];

      // add Bearing Load Ratio only for the first seal in the list
      if (index === 0) {
        displays.push({
          label: "Bearing Load Ratio",
          base: PercentageUnits.Ratio,
          target: PercentageUnits.Percentage,
          model: PercentageUnits.options(),
          value: calcs.bearingLoadRatio,
          mode: "read"
        });
      }

      return {
        label: calcs.xrefSeal?.r_seal?.description || "",
        displays
      };
    }
  )
});
